import React from "react";
import { getSizeClasses, durations, zIndex } from "../../utils/designTokens";

/**
 * UnifiedLoading Component
 * Consolidates all loading patterns into one component
 * Replaces: LoadingSpinner, LoadingOverlay, PageLoader, SkeletonLoader
 */

// Spinner types
const SpinnerType = {
  CIRCULAR: "circular",
  DOTS: "dots",
  PULSE: "pulse",
  SKELETON: "skeleton",
  BAR: "bar",
};

// Loading modes
const LoadingMode = {
  INLINE: "inline", // Inline with content
  OVERLAY: "overlay", // Overlay on container
  FULLSCREEN: "fullscreen", // Full screen overlay
  SKELETON: "skeleton", // Skeleton placeholder
};

const UnifiedLoading = React.memo(
  ({
    // Core props
    mode = LoadingMode.INLINE,
    type = SpinnerType.CIRCULAR,
    size = "md",
    variant = "primary",

    // Content props
    label = "Loading...",
    showLabel = false,
    children,

    // Skeleton props
    skeletonLines = 3,
    skeletonHeight = "h-4",

    // Styling props
    className = "",
    overlayClassName = "",

    // Behavior props
    minDisplayTime = 0,
    isLoading = true,

    ...props
  }) => {
    const [isVisible, setIsVisible] = React.useState(isLoading);
    const loadingStartTimeRef = React.useRef(null);

    // Handle minimum display time
    React.useEffect(() => {
      if (isLoading && !isVisible) {
        loadingStartTimeRef.current = Date.now();
        setIsVisible(true);
      } else if (!isLoading && isVisible) {
        const timeElapsed = Date.now() - (loadingStartTimeRef.current || 0);
        const remainingTime = Math.max(0, minDisplayTime - timeElapsed);

        if (remainingTime > 0) {
          const timer = setTimeout(() => setIsVisible(false), remainingTime);
          return () => clearTimeout(timer);
        } else {
          setIsVisible(false);
        }
      }
    }, [isLoading, isVisible, minDisplayTime]);

    // Size configurations
    const spinnerSizes = {
      xs: "w-3 h-3",
      sm: "w-4 h-4",
      md: "w-6 h-6",
      lg: "w-8 h-8",
      xl: "w-10 h-10",
    };

    const dotSizes = {
      xs: "w-1 h-1",
      sm: "w-1.5 h-1.5",
      md: "w-2 h-2",
      lg: "w-2.5 h-2.5",
      xl: "w-3 h-3",
    };

    // Variant colors
    const variantColors = {
      primary: "text-indigo-600 dark:text-indigo-400",
      secondary: "text-gray-600 dark:text-gray-400",
      success: "text-green-600 dark:text-green-400",
      danger: "text-red-600 dark:text-red-400",
      warning: "text-amber-600 dark:text-amber-400",
      info: "text-blue-600 dark:text-blue-400",
      inherit: "text-current",
    };

    // Render spinner based on type
    const renderSpinner = () => {
      const color = variantColors[variant] || variantColors.primary;

      switch (type) {
        case SpinnerType.CIRCULAR:
          return (
            <svg
              className={`animate-spin ${spinnerSizes[size]} ${color}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          );

        case SpinnerType.DOTS:
          return (
            <div
              className={`flex items-center space-x-1 ${color}`}
              aria-hidden="true"
            >
              {[0, 1, 2].map((dot) => (
                <div
                  key={dot}
                  className={`${dotSizes[size]} rounded-full bg-current animate-bounce`}
                  style={{ animationDelay: `${dot * 150}ms` }}
                />
              ))}
            </div>
          );

        case SpinnerType.PULSE:
          return (
            <div
              className={`relative ${spinnerSizes[size]}`}
              aria-hidden="true"
            >
              <div
                className={`absolute inset-0 rounded-full ${color} opacity-60 animate-ping`}
              />
              <div
                className={`absolute inset-0 rounded-full ${color} opacity-40 animate-ping`}
                style={{ animationDelay: "0.5s" }}
              />
              <div
                className={`relative rounded-full ${color} bg-current h-full w-full`}
              />
            </div>
          );

        case SpinnerType.BAR:
          return (
            <div className="w-full max-w-xs">
              <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${color} bg-current rounded-full animate-loading-bar`}
                  style={{ width: "30%" }}
                />
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    // Render skeleton
    const renderSkeleton = () => {
      return (
        <div className={`animate-pulse ${className}`}>
          {Array.from({ length: skeletonLines }).map((_, index) => (
            <div
              key={index}
              className={`${skeletonHeight} bg-gray-200 dark:bg-gray-700 rounded mb-3 last:mb-0`}
              style={{ width: index === skeletonLines - 1 ? "75%" : "100%" }}
            />
          ))}
        </div>
      );
    };

    // Render based on mode
    if (!isVisible) {
      return mode === LoadingMode.OVERLAY && children ? children : null;
    }

    switch (mode) {
      case LoadingMode.SKELETON:
        return renderSkeleton();

      case LoadingMode.FULLSCREEN:
        return (
          <div
            className={`fixed inset-0 flex items-center justify-center bg-surface/90 animate-fade-in`}
            style={{ zIndex: zIndex.spinner }}
          >
            <div className="text-center bg-surface-container p-8 rounded-xl shadow-2xl animate-scale-in">
              {renderSpinner()}
              {label && showLabel && (
                <p className="mt-4 text-on-surface font-medium animate-fade-in-up">
                  {label}
                </p>
              )}
              {!showLabel && <span className="sr-only">{label}</span>}
            </div>
          </div>
        );

      case LoadingMode.OVERLAY:
        return (
          <div className={`relative ${className}`} {...props}>
            {children}
            <div
              className={`absolute inset-0 flex items-center justify-center bg-surface/80 transition-opacity ${
                isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
              } ${overlayClassName}`}
              style={{ zIndex: zIndex.dropdown }}
            >
              <div className="flex flex-col items-center">
                {renderSpinner()}
                {label && showLabel && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {label}
                  </p>
                )}
                {!showLabel && <span className="sr-only">{label}</span>}
              </div>
            </div>
          </div>
        );

      case LoadingMode.INLINE:
      default:
        return (
          <div
            className={`inline-flex flex-col items-center justify-center ${className}`}
            {...props}
          >
            {renderSpinner()}
            {label && showLabel && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {label}
              </p>
            )}
            {!showLabel && <span className="sr-only">{label}</span>}
          </div>
        );
    }
  },
);

UnifiedLoading.displayName = "UnifiedLoading";

// Export modes and types for external use
export { LoadingMode, SpinnerType };

// Helper components for common use cases
export const InlineLoader = (props) => (
  <UnifiedLoading mode={LoadingMode.INLINE} {...props} />
);

export const OverlayLoader = (props) => (
  <UnifiedLoading mode={LoadingMode.OVERLAY} {...props} />
);

export const PageLoader = (props) => (
  <UnifiedLoading mode={LoadingMode.FULLSCREEN} showLabel {...props} />
);

export const SkeletonLoader = (props) => (
  <UnifiedLoading mode={LoadingMode.SKELETON} {...props} />
);

// Add required CSS for bar animation
const style = document.createElement("style");
style.textContent = `
  @keyframes loading-bar {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(400%); }
  }
  .animate-loading-bar {
    animation: loading-bar 1.5s ease-in-out infinite;
  }
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .animate-fade-in {
    animation: fade-in ${durations.normal} ease-out;
  }
  @keyframes fade-in-up {
    from { 
      opacity: 0;
      transform: translateY(10px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fade-in-up {
    animation: fade-in-up ${durations.normal} ease-out;
  }
  @keyframes scale-in {
    from { 
      opacity: 0;
      transform: scale(0.95);
    }
    to { 
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-scale-in {
    animation: scale-in ${durations.normal} ease-out;
  }
`;
document.head.appendChild(style);

export default UnifiedLoading;
